import { useFlags } from '@atlaskit/flag';
import { createRandomDelivery, IDelivery } from 'core/api/deliveries/deliveries-api-interface';
import { DeliveryStatuses, getDeliveryStatusNameFromKey } from 'core/constants/delivery-status';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import dayjs from 'dayjs';
import { orderBy, QueryConstraint, where } from 'firebase/firestore';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import {
  ISharedTableColumn,
  ISharedTableCustomCellTemplate,
  ISharedTableRow,
} from 'shared/components/table/table-interface';
import { DeliveryTypes, getDeliveryTypeNameFromKey } from 'core/constants/delivery-types';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@atlaskit/checkbox';
import { useAuthState } from 'core/providers/AuthProvider';
import ExportDeliveriesDialog from '../export-deliveries-dialog/export-deliveries-dialog';
import AddEditDeliveryDialog from '../add-edit-delivery-dialog/add-edit-delivery-dialog';
import { getWasteCollectionNameFromKey } from 'core/constants/waste-collection';
import { getStorageRequirementNameFromKey } from 'core/constants/storage-requirement';
import { getSharpsBinOptionNameFromKey } from 'core/constants/sharps-bin-options';
import { useDeliveriesState } from 'core/providers/DeliveriesProvider';
import {
  DeliveryRouteAssignment,
  getDeliveryRouteAssignmentNameFromKey,
} from 'core/constants/delivery-route-assignment';
import { getRouteOptionNameFromKey } from 'core/constants/route-options';
import { Role } from 'core/config/roles';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { useCollection } from 'core/hooks/useCollection';
import { IDeliveryDao } from 'core/api/types/deliveries.interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { mapPropertyToFirestore } from 'core/utilities/filter-mapper';
import { IButton } from 'shared/components/buttons/button-interface';
import { DeliveriesApiService } from 'core/api';
import LegacyDeliveriesApiService from 'core/api/deliveries/deliveries-api.service';

const DeliveryTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Customer Name', key: 'customerName', templateId: 'text', width: 10 },
  { label: 'Postcode', key: 'postcode', templateId: 'text', width: 10 },
  { label: 'Parcels', key: 'parcelCount', templateId: 'text', width: 5, isCompact: true },
  { label: 'Sharps bin', key: 'sharpsBin', templateId: 'text', width: 10 },
  { label: 'Waste collection', key: 'wasteCollection', templateId: 'text', width: 5 },
  { label: 'Storage', key: 'storageRequirement', templateId: 'text', width: 10 },
  { label: 'Delivery Date', key: 'date', templateId: 'text', width: 10 },
  { label: 'Status', key: 'status', templateId: 'text', width: 10 },
  { label: 'Type', key: 'type', templateId: 'text', width: 10 },
  { label: 'Route pot', key: 'route', templateId: 'text', width: 10 },
  { label: 'Allocated route', key: 'assignedRoute', templateId: 'text', width: 10 },
  { label: '', key: 'actions', templateId: 'actions', width: 9, isCompact: true },
];

const deliveryTypesOptions = DeliveryTypes.map((type) => ({ value: type.key, label: type.label }));

const deliveryStatusOptions = DeliveryStatuses.map((status) => ({ value: status.key, label: status.label }));

const DeliveriesList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();
  const { userRoles } = useAuthState();
  const { todaysDeliveryCount, tomorrowsDeliveryCount } = useDeliveriesState();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [tableRows, setTableRows] = useState<ISharedTableRow<IDeliveryDao>[]>([]);

  const { data, loading, loadMore, hasMore, loadingMore } = useCollection<IDeliveryDao>({
    apiService: DeliveriesApiService,
    constraints: [...constraints, orderBy('orderByDate', 'desc'), orderBy('address.town_or_city')],
  });

  useEffect(() => {
    if (data) {
      setTableRows(
        data.map((data) => ({
          key: data.uid,
          data: {
            ...data,
            customerName: `${data.firstName} ${data.lastName}`,
            date: dayjs(data.date).format('DD/MM/YYYY'),
            type: getDeliveryTypeNameFromKey(data.type),
            postcode: data.address.postcode,
            status: getDeliveryStatusNameFromKey(data.status),
            wasteCollection: getWasteCollectionNameFromKey(data.wasteCollection),
            storageRequirement: getStorageRequirementNameFromKey(data.storageRequirement),
            sharpsBin: getSharpsBinOptionNameFromKey(data.sharpsBin),
            route: getDeliveryRouteAssignmentNameFromKey(data.route),
            assignedRoute: getAllocatedRoute(data.assignedRoute),
          },
        })),
      );
    }
  }, [data]);

  useEffect(() => {
    setSelectedRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '')
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(mapPropertyToFirestore(filter.id), '==', value);
        });

      setConstraints(newConstraints);
    }, 1500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery],
  );

  const getAllocatedRoute = (key?: string) => {
    if (key === DeliveryRouteAssignment.NONE) {
      return 'Unallocated';
    }

    return key ? getRouteOptionNameFromKey(key) : 'Unknown';
  };

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (delivery: IDelivery) => {
    return <Checkbox isChecked={selectedRows.includes(delivery.uid)} onChange={(e) => rowChecked(e, delivery.uid)} />;
  };

  const actionCellTemplate = (delivery: IDelivery) => {
    return (
      <div className='w-full flex justify-end'>
        <SharedButton
          onClick={() => navigate(delivery.uid)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='View'
        />
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ConfirmationDialog
                title='Delete delivery'
                textContent='Are you sure you want to delete this delivery? This action cannot be undone.'
                action={() => deleteItem(DeliveriesApiService, delivery, flags, dialog)}
                buttonLabel='Delete Delivery'
              />,
            )
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Delete'
        />
      </div>
    );
  };

  const headerActions: PageElement[] = [
    ...(process.env.NODE_ENV === 'development'
      ? [
          {
            key: 'addFakeDelivery',
            element: (
              <SharedButton
                onClick={async () => {
                  const fake = createRandomDelivery(dayjs().add(1, 'day').toDate());
                  await LegacyDeliveriesApiService.add(fake);
                }}
                type='button'
                label='Add fake delivery'
                appearance='default'
              />
            ),
          },
        ]
      : []),
    ...([Role.PHARMACY_ADMIN, Role.SUPER_ADMIN].some((role) => userRoles?.includes(role))
      ? [
          {
            key: 'exportDeliveries',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<ExportDeliveriesDialog />)}
                type='button'
                appearance='default'
                label='Export deliveries'
              />
            ),
          },
        ]
      : []),
    ...(selectedRows.length
      ? [
          {
            key: 'deleteDeliveries',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete deliveries'
                      textContent='Are you sure you want to delete these deliveries? This action cannot be undone.'
                      action={() => deleteItems(DeliveriesApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Deliveries'
                    />,
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Deliveries'
              />
            ),
          },
        ]
      : [
          {
            key: 'addDelivery',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditDeliveryDialog />)}
                type='button'
                appearance='primary'
                label='Add delivery'
              />
            ),
          },
        ]),
  ];

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'actions',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const filters: Filter[] = [
    {
      id: 'lastName',
      label: 'Customer last name',
    },
    {
      id: 'status',
      label: 'Delivery status',
      options: deliveryStatusOptions,
    },
    {
      id: 'date',
      label: 'Delivery date',
      date: true,
    },
    {
      id: 'type',
      label: 'Delivery type',
      options: deliveryTypesOptions,
    },
    {
      id: 'postcode',
      label: 'Delivery postcode',
    },
    {
      id: 'hospitalNumber',
      label: 'Hospital number',
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Deliveries' actions={headerActions} />
      <div className='flex py-4 border-b body-02'>
        <p>
          Deliveries today: <span className='font-semibold'>{todaysDeliveryCount}</span>
        </p>
        <p className='border-l ml-2 pl-2'>
          Deliveries tomorrow: <span className='font-semibold'>{tomorrowsDeliveryCount}</span>
        </p>
      </div>
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} limit={2} />
      <SharedTable
        columns={DeliveryTableColumns}
        rows={tableRows}
        isLoading={loading}
        customTemplates={customTemplates}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default DeliveriesList;
